import { caravanMarkVisited } from '@helpers/caravan/caravan';
import {
  combatantsFromTownGuardians,
  combatCreateForEncounter,
} from '@helpers/combat/combat-create';
import {
  CHARACTER_MAX_LEVEL,
  PARTY_SIZE_MAX,
  TRADESKILL_MAX_LEVEL,
  WORKER_MAX_LEVEL,
} from '@helpers/config';
import { getEntriesByType, getEntry } from '@helpers/content/content';
import {
  isRecipeDropGated,
  recipeDiscover,
  recipeUndiscover,
} from '@helpers/crafting/recipes';
import {
  tradeskillBuildingIn,
  tradeskillIdForName,
  tradeskillXpForLevel,
} from '@helpers/crafting/tradeskill';
import {
  characterRecalculateStats,
  characterXpForLevel,
  createCharacter,
  setParty,
} from '@helpers/hero/party';
import {
  characterSkillTreeCanLearn,
  skillTreePrune,
} from '@helpers/hero/skill-tree';
import { travelRelocateTo } from '@helpers/hero/travel';
import { collectiblesAdd } from '@helpers/item/collectibles';
import { gatherNodeDiscover } from '@helpers/item/gather-node-discovery';
import { addMaterial } from '@helpers/item/materials';
import { armoryAdd, armoryAddWithAffixes } from '@helpers/kingdom/armory';
import {
  monsterEncounters,
  monsterRecordKill,
} from '@helpers/kingdom/bestiary';
import {
  updateGamestate,
  worldPartyState,
  worldTownsState,
} from '@helpers/state-game';
import { timerTicksElapsed } from '@helpers/engine/timer';
import { raidAssaulterMonsterIds } from '@helpers/town/raid/town-raid-state';
import { telegraphRaid } from '@helpers/town/raid/town-raid-tick';
import {
  TOWN_REPUTATION_THRESHOLDS,
  townReputationTierForAmount,
} from '@helpers/town/reputation/town-reputation';
import { townCommissionRefreshTierScaledSlots } from '@helpers/town/town-commission-generate';
import { townGuardiansForCurrentReputation } from '@helpers/town/town-guardian';
import { townMarkVisited } from '@helpers/town/town-visit';
import { tutorialUnmarkSeen } from '@helpers/tutorial/tutorial-seen';
import { workerRescue } from '@helpers/worker/worker-discovery';
import { workerXpForLevel } from '@helpers/worker/worker-progression';
import { worldNodeDevelopmentMaxLevel } from '@helpers/world-node/world-node-development';
import {
  WORLD_NODE_DEVELOPABLE_TYPES,
  WORLD_NODE_DEVELOPMENT_SLICES,
} from '@helpers/world-node/world-node-development-state';
import {
  worldNodeByName,
  worldNodeGathering,
} from '@helpers/world-node/world-nodes';
import {
  type AffixContent,
  type AffixId,
  type CaravanId,
  type Character,
  type CharacterId,
  type CollectibleContent,
  type CollectibleId,
  type EquipmentContent,
  type EquipmentId,
  type ItemContent,
  type ItemId,
  type SkillTreeNodeId,
  type MonsterContent,
  type RecipeContent,
  type RecipeId,
  type TownContent,
  type TownId,
  type Tradeskill,
  type TutorialId,
  type WorkerContent,
  type WorkerId,
} from '@interfaces';
import { clamp } from 'es-toolkit/compat';
import {
  taskEventLevelReached,
  taskEventTownReputationTier,
  taskEventTradeskillLevel,
} from '@helpers/task/task-events';

export function debugGiveItem(itemId: ItemId, quantity: number): void {
  if (quantity <= 0) return;
  const material = getEntry<ItemContent>(itemId);
  if (!material) {
    console.warn(`Item with ID ${itemId} not found.`);
    return;
  }

  if (material.unobtainable) {
    console.warn(`Item with ID ${itemId} not obtainable.`);
    return;
  }

  addMaterial(material.id, quantity);
}

export function debugGiveEquipment(
  equipmentId: EquipmentId,
  quantity: number,
): void {
  if (quantity <= 0) return;

  const equipment = getEntry<EquipmentContent>(equipmentId);
  if (!equipment) {
    console.warn(`Equipment with ID ${equipmentId} not found.`);
    return;
  }

  if (equipment.unobtainable) {
    console.warn(`Equipment with ID ${equipmentId} not obtainable.`);
    return;
  }

  armoryAdd(equipment.id, quantity, false, true);
}

// Bypasses the normal rarity-based affix roll so a specific combination can be tested on demand.
export function debugGiveEquipmentWithAffixes(
  equipmentId: EquipmentId,
  affixIds: AffixId[],
): void {
  const equipment = getEntry<EquipmentContent>(equipmentId);
  if (!equipment) {
    console.warn(`Equipment with ID ${equipmentId} not found.`);
    return;
  }

  if (equipment.unobtainable) {
    console.warn(`Equipment with ID ${equipmentId} not obtainable.`);
    return;
  }

  const unknownAffixId = affixIds.find(
    (affixId) => getEntry<AffixContent>(affixId)?.__type !== 'affix',
  );
  if (unknownAffixId) {
    console.warn(`Affix with ID ${unknownAffixId} not found.`);
    return;
  }

  armoryAddWithAffixes(equipment.id, affixIds);
}

export function debugSetCharacterLevel(
  characterId: CharacterId,
  level: number,
): void {
  const clampedLevel = clamp(Math.round(level), 1, CHARACTER_MAX_LEVEL);
  if (!worldPartyState().some((character) => character.id === characterId)) {
    return;
  }

  debugUpdateCharacter(characterId, (character) => ({
    ...character,
    level: clampedLevel,
    xp: { current: 0, maximum: characterXpForLevel(clampedLevel) },
    skillTreeNodes: {
      ...character.skillTreeNodes,
      [character.jobId]: skillTreePrune(
        character.jobId,
        character.skillTreeNodes[character.jobId] ?? [],
        clampedLevel,
      ),
    },
  }));
  void taskEventLevelReached(clampedLevel);
}

export function debugSetPartySize(size: number): void {
  const party = worldPartyState();
  if (party.length === 0 || !Number.isFinite(size)) return;

  const target = clamp(Math.round(size), 1, PARTY_SIZE_MAX);
  const added = Array.from(
    { length: Math.max(0, target - party.length) },
    (_, i) => createCharacter(`Hero ${party.length + i + 1}`, party[0].jobId),
  );
  setParty([...party.slice(0, target), ...added]);
}

function debugUpdateCharacter(
  characterId: CharacterId,
  update: (character: Character) => Character,
): void {
  updateGamestate((state) => {
    state.world.party = state.world.party.map((character) =>
      character.id === characterId
        ? characterRecalculateStats(update(character))
        : character,
    );

    return state;
  });
}

// Learns a node in the hero's current job tree if it's learnable and an SP is free.
export function debugLearnSkillTreeNode(
  characterId: CharacterId,
  nodeId: SkillTreeNodeId,
): void {
  const character = worldPartyState().find((c) => c.id === characterId);
  if (!character || !characterSkillTreeCanLearn(character, nodeId)) {
    console.warn(`Skill tree node ${nodeId} can't be learned right now.`);
    return;
  }

  debugUpdateCharacter(characterId, (c) => ({
    ...c,
    skillTreeNodes: {
      ...c.skillTreeNodes,
      [c.jobId]: [...(c.skillTreeNodes[c.jobId] ?? []), nodeId],
    },
  }));
}

export function debugSetTradeskillLevel(
  tradeskill: Tradeskill,
  level: number,
): void {
  const tradeskillId = tradeskillIdForName(tradeskill);
  if (!tradeskillId) return;

  const clampedLevel = clamp(Math.round(level), 1, TRADESKILL_MAX_LEVEL);

  updateGamestate((state) => {
    state.tradeskills[tradeskillId] = {
      ...tradeskillBuildingIn(state, tradeskillId),
      level: clampedLevel,
      xp: { current: 0, maximum: tradeskillXpForLevel(clampedLevel) },
    };

    return state;
  });
  void taskEventTradeskillLevel(tradeskillId, clampedLevel);
}

// Wipes every bestiary discovery/kill record - primarily a recovery tool
// for a save whose bestiary data was corrupted by a since-fixed bug (e.g.
// NaN level ranges from before min/max level tracking existed).
export function debugResetBestiary(): void {
  updateGamestate((state) => {
    state.bestiary = {};
    return state;
  });
}

// Clears every hero's teachings for every job; stats are recomputed so the lost bonuses come off immediately.
export function debugResetHeroTeachings(): void {
  updateGamestate((state) => {
    state.world.party = state.world.party.map((character) =>
      characterRecalculateStats({ ...character, teachings: {} }),
    );

    return state;
  });
}

// Records a kill for every monster at every node/level it's fought at, so
// the bestiary shows real stat spreads without fighting everything manually.
export function debugFillBestiary(): void {
  getEntriesByType<MonsterContent>('monster').forEach((monster) => {
    const encounters = monsterEncounters(monster.id);

    if (encounters.length === 0) {
      monsterRecordKill(monster.id, 1);
      return;
    }

    encounters.forEach((encounter) => {
      monsterRecordKill(monster.id, encounter.levelRange.min, encounter.name);
      monsterRecordKill(monster.id, encounter.levelRange.max, encounter.name);
    });
  });
}

// Clears every caravan's commission state so it regenerates on the next
// visit/tick - a recovery tool for a commission stuck blank.
export function debugResetCommissions(): void {
  updateGamestate((state) => {
    state.world.commissions = {};
    return state;
  });
}

export function debugResetTutorial(tutorialId: TutorialId): void {
  tutorialUnmarkSeen(tutorialId);
}

export function debugResetAllTutorials(): void {
  updateGamestate((state) => {
    state.tutorials = {};
    return state;
  });
}

// Marks a GatherNode as visited without walking the party there - workers can only be assigned to nodes discovered this way.
export function debugDiscoverGatherNode(nodeName: string): void {
  gatherNodeDiscover(nodeName);
}

export function debugGiveCollectible(
  collectibleId: CollectibleId,
  quantity: number,
): void {
  if (quantity <= 0) return;

  const collectible = getEntry<CollectibleContent>(collectibleId);
  if (!collectible) {
    console.warn(`Collectible with ID ${collectibleId} not found.`);
    return;
  }

  if (collectible.unobtainable) {
    console.warn(`Collectible with ID ${collectibleId} not obtainable.`);
    return;
  }

  collectiblesAdd(collectible.id, quantity);
}

export function debugDiscoverAllCollectibles(): void {
  getEntriesByType<CollectibleContent>('collectible').forEach((collectible) => {
    collectiblesAdd(collectible.id, 1);
  });
}

// Discovers every drop-gated recipe so it becomes craftable; non-drop-gated
// recipes need no discovery record.
export function debugDiscoverAllRecipes(): void {
  getEntriesByType<RecipeContent>('recipe')
    .filter((recipe) => isRecipeDropGated(recipe.id))
    .forEach((recipe) => {
      recipeDiscover(recipe.id);
    });
}

// Reverts a single drop-gated recipe back to undiscovered - a testing tool
// for re-triggering discovery/unlock flows without waiting on a real drop.
export function debugUndiscoverRecipe(recipeId: RecipeId): void {
  const recipe = getEntry<RecipeContent>(recipeId);
  if (!recipe) {
    console.warn(`Recipe with ID ${recipeId} not found.`);
    return;
  }

  recipeUndiscover(recipe.id);
}

export function debugRescueWorker(workerId: WorkerId): void {
  const worker = getEntry<WorkerContent>(workerId);
  if (!worker) {
    console.warn(`Worker with ID ${workerId} not found.`);
    return;
  }

  workerRescue(worker.id);
}

export function debugSetWorkerLevel(workerId: WorkerId, level: number): void {
  const clampedLevel = clamp(Math.round(level), 1, WORKER_MAX_LEVEL);

  updateGamestate((state) => {
    const worker = state.workers[workerId];
    if (!worker) return state;

    worker.level = clampedLevel;
    worker.xp = { current: 0, maximum: workerXpForLevel(clampedLevel) };

    return state;
  });
}

export async function debugSetTownReputation(
  townId: TownId,
  reputation: number,
): Promise<void> {
  const realTown = getEntry<TownContent>(townId);
  if (!realTown) {
    console.warn(`Could not find a town with matching id ${townId}.`);
    return;
  }

  const realTownId = realTown.id;

  const clamped = clamp(
    Math.round(reputation),
    0,
    TOWN_REPUTATION_THRESHOLDS[4],
  );

  await updateGamestate((state) => {
    const town = state.world.towns[realTownId];
    if (!town) return state;

    town.reputation = clamped;

    return state;
  });

  // Debug-set reputation skips townReputationGain/Lose, so tier-scaled persistent commissions need an explicit refresh here too.
  await townCommissionRefreshTierScaledSlots(realTownId);
  void taskEventTownReputationTier(
    realTownId,
    townReputationTierForAmount(clamped),
  );
}

// Raid combat from anywhere, no telegraph/standing-at-town required.
export function debugStartTownDefenseCombat(townId: TownId): void {
  const town = getEntry<TownContent>(townId);
  if (!town) {
    console.warn(`Could not find a town with matching id ${townId}.`);
    return;
  }

  if (town.defense.assaulter.monsterIds.length === 0) {
    console.warn(`Town ${town.name} has no assaulter monsters authored.`);
    return;
  }

  const helpers = combatantsFromTownGuardians(
    townGuardiansForCurrentReputation(town),
    town.level,
  );

  const enemies = raidAssaulterMonsterIds(town.defense.assaulter)
    .map((monsterId) => getEntry<MonsterContent>(monsterId))
    .filter((monster): monster is MonsterContent => !!monster);

  const combat = {
    ...combatCreateForEncounter(
      worldPartyState(),
      enemies,
      town.defense.assaulter.level.max,
      town.name,
      helpers,
    ),
    raidTownId: town.id,
  };

  updateGamestate((state) => {
    state.world.combat = combat;
    return state;
  });
}

// Forces a telegraph via the real write path, skipping the visited/cooldown/anti-rush gates.
export function debugTelegraphRaid(townId: TownId): void {
  const town = getEntry<TownContent>(townId);
  if (!town) {
    console.warn(`Could not find a town with matching id ${townId}.`);
    return;
  }

  if (worldTownsState()[townId]?.firstVisitedAtTick === undefined) {
    townMarkVisited(townId);
  }

  telegraphRaid(town);
}

export function debugTeleportToNode(nodeName: string): void {
  const node = worldNodeByName(nodeName);
  if (!node) {
    console.warn(`Node "${nodeName}" not found.`);
    return;
  }

  travelRelocateTo(node);
}

export function debugSetGatherNodeLevel(nodeName: string, level: number): void {
  const node = worldNodeByName(nodeName);
  const gathering = node ? worldNodeGathering(node) : undefined;
  if (!gathering) {
    console.warn(`Gather node "${nodeName}" not found.`);
    return;
  }

  const maxLevel = worldNodeDevelopmentMaxLevel(gathering);
  const clampedLevel = clamp(Math.round(level), 0, maxLevel);

  // A pending development survives unless it would push the node past max.
  updateGamestate((state) => {
    const pending =
      clampedLevel < maxLevel
        ? state.gatherNodeLevels[nodeName]?.developCompletesAtTick
        : undefined;
    state.gatherNodeLevels[nodeName] =
      pending === undefined
        ? { level: clampedLevel }
        : { level: clampedLevel, developCompletesAtTick: pending };
    return state;
  });
}

// Finishes on the next tick, so completion notices and side effects still fire normally.
export function debugCompleteWorldNodeDevelopments(): void {
  const nowTick = timerTicksElapsed();
  updateGamestate((state) => {
    WORLD_NODE_DEVELOPABLE_TYPES.forEach((type) => {
      Object.values(state[WORLD_NODE_DEVELOPMENT_SLICES[type]]).forEach(
        (entry) => {
          if (entry.developCompletesAtTick === undefined) return;
          entry.developCompletesAtTick = nowTick;
        },
      );
    });
    return state;
  });
}

export function debugMarkTownVisited(townId: TownId): void {
  townMarkVisited(townId);
}

export function debugMarkCaravanVisited(caravanId: CaravanId): void {
  caravanMarkVisited(caravanId);
}
